import { useTheme } from '@mui/material/styles';
import OutlinedInput, { OutlinedInputProps } from '@mui/material/OutlinedInput';
import { ReactNode, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Alert from '../alert';

// const useStyles = makeStyles({
//     tess: {
//       position: 'relative'
//     },
//   });

interface IProps extends OutlinedInputProps {
    children?: ReactNode;
    label?: ReactNode;
    subLabel?: string;
    boldLabel?: boolean;
    fontSize?: number;
    error?: boolean;
    testId?: string;
    errorMessage?: string;
    className?: string;
    labelClassName?: string;
    endContent?: string | ReactNode;
    startContent?: string | ReactNode;
    maxLength?: number;
    enableNaturalEnter?: boolean;
}

export const Input = forwardRef<HTMLInputElement, IProps>(
    (
        {
            label,
            subLabel,
            boldLabel = false,
            size = 'small',
            required,
            type = 'text',
            value,
            autoFocus,
            fontSize = 18,
            error,
            errorMessage,
            className = '',
            labelClassName = '',
            sx,
            endContent,
            startContent,
            maxLength,
            enableNaturalEnter,
            testId = 'base-input',
            ...props
        },
        _ref
    ) => {
        const [showPassword, setShowPassword] = useState(false);
        const [isFocused, setIsFocused] = useState(false);
        const ref = useRef<HTMLInputElement>(null);
        const theme = useTheme();

        const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && !enableNaturalEnter) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, [enableNaturalEnter]);

        useImperativeHandle(_ref, () => ref.current!);

        useEffect(() => {
            if (ref.current && autoFocus) {
                const inp = ref.current.querySelector('input');
                if (inp) inp.focus();
            }
        }, [ref, autoFocus]);

        return (
            <div className={`w-full ${className}`} data-testid={`${testId}-wrapper`}>
                <div className="mb-2 flex flex-col gap-1" data-testid={`${testId}-title-wrapper`}>
                    <div className="flex items-center gap-2" data-testid={`${testId}-label`}>
                        {required && <span className="font-bold text-red-500">*</span>}
                        <span
                            className={`flex flex-row ${boldLabel ? 'text-base font-bold lg:text-lg' : 'text-lg lg:text-xl'} ${labelClassName} font-atkinsonHyperlegible`}
                        >
                            {label}
                        </span>
                    </div>
                    {subLabel && <span className="font-dmSans text-sm lg:text-base" data-testid={`${testId}-sub-label`}>{subLabel}</span>}
                </div>

                <div className="relative" data-testid={`${testId}-content`}>
                    <OutlinedInput
                        size={size}
                        onKeyDown={handleKeyDown}
                        slotProps={{
                            input: {
                                'aria-label': 'input'
                            }
                        }}
                        type={showPassword ? 'text' : type}
                        value={value}
                        ref={ref}
                        autoFocus={autoFocus}
                        error={error}
                        onFocus={() => setIsFocused(true)}
                        data-testid={`${testId}-outer-input`}
                        inputProps={{
                            maxLength: maxLength || Number.MAX_SAFE_INTEGER,
                            "data-testid": `${testId}-input`,
                        }}
                        {...props}
                        onBlur={e => {
                            setIsFocused(false);
                            props.onBlur?.(e || '');
                        }}
                        sx={{
                            ...(type === 'number' && {
                                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                    display: 'none'
                                }
                            }),
                            boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.1)',
                            fieldset: {
                                border: `1px solid rgba(0, 0, 0, ${value ? 0.5 : 0.2})`
                            },
                            outline: 'none',
                            input: {
                                fontSize,
                                padding: startContent && !isFocused ? '8.5px 8.5px  8.5px 50px' : '8.5px',
                                paddingRight: type === 'password' && value ? '3.375rem' : '',
                                [theme.breakpoints.up('md')]: {
                                    padding: startContent && !isFocused ? '9.5px 9.5px  9.5px 50px' : '9.5px',
                                    paddingRight: type === 'password' && value ? '3.375rem' : '',
                                },
                                letterSpacing: type === 'password' && !showPassword ? '3px' : '',
                                '::placeholder': {
                                    letterSpacing: type === 'password' ? '3px' : '',
                                    fontFamily: 'DM Sans, sans-serif'
                                }
                            },
                            ...sx
                        }}
                    />
                    {type === 'password' && !!value && (
                        <span
                            className="absolute bottom-3.5 right-3 cursor-pointer text-sm text-black underline"
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? 'Hide' : 'Show'}
                        </span>
                    )}
                    {endContent && <div className="absolute right-4.5 top-1/2 -translate-y-1/2">{endContent}</div>}
                    {startContent && !isFocused && <div className="absolute left-4.5 top-1/2 -translate-y-1/2">{startContent}</div>}
                </div>

                {error && errorMessage && <Alert className="mt-2.5 font-dmSans lg:mt-6" label={errorMessage} />}
            </div>
        );
    }
);

export default Input;
